import axios, { AxiosError } from "axios";

import { toast } from "vue-sonner";

import { defineStore } from "@/lib/tellscale/pinia.ts";

import { calculateExpiryFromTTL, calculateMsUntilExpiry } from "@/lib/tellscale/token-expiry.ts";

import type { AuthInitResponse, AuthRefreshResponse, Promoter, PromoterResponse, LoginFlowData } from "./types.ts";

import { useRecommendationPortal } from "@/stores/portal";

import { useCrossTabAuth } from "@/composables/useCrossTabAuth";

import { useApi } from "@/stores/api";

import router from "@/router";

/**
 * Authentication store managing promoter login state and passwordless authentication flow
 */
export const useAuth = defineStore('auth', () => {
    const promoter = ref<Promoter>();

    const portal = useRecommendationPortal();

    const api = useApi();

    const loginFlowData = ref<LoginFlowData>();

    const expiresAt = useLocalStorage<number | null>('ts_promoter_auth_expires_at', null);
    const isRefreshing = useLocalStorage<boolean>('ts_promoter_refreshing', false);

    const { t } = useI18n();

    const { broadcastAuthSuccess, broadcastLogout } = useCrossTabAuth();

    const isAuthenticated = computed<boolean>(() => !!promoter.value);

    const ownerEmail = computed<string | undefined>(() => promoter.value?.owner_email);

    const tokenExpiryTimeout = ref<number>();

    /**
     * Attempt to authenticate the promoter (e.g., via session/token)
     *
     * @param {boolean} silent - Whether to broadcast authentication success event
     *
     * @returns {Promise<boolean>} True if authentication successful, false otherwise
     */
    const authenticate = async (silent: boolean = false): Promise<boolean> => {
        console.info('[Auth] Trying to authenticate the user');

        try {
            const { data } = await axios.get<PromoterResponse>('/api/me');

            promoter.value = data.user;

            expiresAt.value = calculateExpiryFromTTL(data.expires_in);

            if(!silent && promoter.value) {
                broadcastAuthSuccess({
                    id: promoter.value.id,
                    email: promoter.value.email
                });
            }

            loginFlowData.value = undefined;

            console.info('[Auth] User authenticated');

            return true;
        }
        catch(error: AxiosError | any) {
            if(error.status === 401) return false;

            console.error(error);

            toast.error(t('errors.authentication'));

            return false;
        }
    }

    /**
     * Authenticate the promoter using a verification code sent via email
     *
     * @param {number} promoterId - Promoter id
     * @param {string} code - Verification code from email
     * @returns {Promise<boolean>} True if login successful, false otherwise
     */
    const loginViaCode = async (promoterId: number, code: string): Promise<boolean> => {
        try {
            if(!portal.value) {
                throw('No portal or promoter found to login via code');
            }

            const portalId = portal.value.id;
            const portalCode = portal.value.code;

            await api.app.get(`/api/auth/promotor/login/${promoterId}/${code}/${portalId}/${portalCode}`, {
                headers: { 'Accept': 'application/json' }
            });

            await authenticate();

            return true;
        }
        catch(error: AxiosError | any) {
            if(error.status !== 401) {
                console.error(error);

                toast.error(t('errors.loginViaCode'));
            }

            return false;
        }
    }

    /**
     * Send a magic login link to the promoter's email address
     *
     * @param {string} email - Promoter email address
     *
     * @returns {Promise<boolean>} True if a login link sent successfully, false otherwise
     */
    const sendLoginLink = async (email: string): Promise<boolean> => {
        if(isAuthenticated.value) return false;

        if(!portal.isLoaded) {
            throw('No portal found to send login link');
        }

        try {
            const { data } = await axios.post<AuthInitResponse>('/api/recommendation/portal/auth/init', {
                recommendation_portal_id: portal.value!.id,
                link_extension: portal.value!.code,
                promoter_email: email,
            });

            loginFlowData.value = {
                id: data.promoter_id,
                email: email,
            };

            return true;
        }
        catch(error) {
            console.error(error);

            toast.error(t('errors.sendLoginLink'));

            return false;
        }
    }

    /**
     * Send a verification code to the promoter's email address
     *
     * @param {string} email - Promoter email address
     *
     * @returns {Promise<boolean>} True if a login code sent successfully, false otherwise
     */
    const sendLoginCode = async (email: string): Promise<boolean> => {
        if(isAuthenticated.value) return false;

        try {
            const { data } = await axios.post<AuthInitResponse>('/api/recommendation/portal/auth/init', {
                recommendation_portal_id: portal.value!.id,
                link_extension: portal.value!.code,
                promoter_email: email,
                send_code: true,
            });

            loginFlowData.value = {
                id: data.promoter_id,
                email: email,
            };

            return true;
        }
        catch(error) {
            console.error(error);

            toast.error(t('errors.sendLoginCode'));

            return false;
        }
    }

    /**
     * Logout the current promoter and clear the session
     *
     * @param {boolean} silent - Whether to broadcast logout event
     */
    const logout = async (silent: boolean = false): Promise<void> => {
        console.info('[Auth] Promoter logout initiated');

        try {
            await api.app.post('/api/auth/promoter/logout');
        }
        catch(error: AxiosError | any) {
            if(error.status !== 401) {
                console.error(error);

                toast.error(t('errors.logout'));

                return;
            }
        }
        finally {
            promoter.value = undefined;

            expiresAt.value = null;

            if(!silent) {
                broadcastLogout();
            }

            console.info('[Auth] Promoter logout successful');

            await router.push('/portal/login');
        }
    }

    /**
     * Refresh the current promoter token
     */
    const refresh = async (): Promise<void> => {
        console.info('[Auth] Promoter refresh initiated');

        if(isRefreshing.value) {
            console.info('[Auth] Currently another tab is refreshing, skipping to avoid race conditions');
            return;
        }

        isRefreshing.value = true;

        try {
            const { data } = await api.app.post<AuthRefreshResponse>('/api/auth/promoter/refresh');

            expiresAt.value = calculateExpiryFromTTL(data.expires_in);

            console.info('[Auth] Promoter refresh successful');

            isRefreshing.value = false;
        }
        catch(error) {
            console.error(error);

            toast.error(t('errors.refresh'));

            isRefreshing.value = false;

            return await logout();
        }
    }

    /**
     * Handle changes to the token expiry value and start expiry timeout
     *
     * @param {number | null} expiresAt - New Token expiry timestamp
     */
    const handleTokenExpiryChange = (expiresAt: number | null) => {
        clearTimeout(tokenExpiryTimeout.value);

        if(!expiresAt) return;

        console.info('[Auth] Token expiry change detected, starting expiry timeout');

        const msUntilExpiry = calculateMsUntilExpiry(expiresAt);

        tokenExpiryTimeout.value = setTimeout(async () => {
            console.info('[Auth] Token expiry detected, starting auto-logout');

            await logout();
        }, msUntilExpiry);
    }

    watch(expiresAt, (value) => handleTokenExpiryChange(value), { immediate: true });

    /**
     * Reset store to initial state
     */
    const reset = (): void => {
        promoter.value = undefined;
        loginFlowData.value = undefined;
        expiresAt.value = null;
        isRefreshing.value = false;
    }

    return {
        promoter, isAuthenticated, ownerEmail, loginFlowData, expiresAt,
        authenticate, sendLoginLink, sendLoginCode, loginViaCode, logout, refresh, reset,
    };
});